import type { PendingAttempt } from 'modules/grading/api';
import type { DatePreset } from './useAnalyticsFilters';

/** i18n key naming each reporting period ("So'nggi 30 kun", "Bugun" …). */
export const PERIOD_LABEL_KEY: Record<DatePreset, string> = {
  today: 'analytics_range_today',
  '7d': 'last_7_days',
  '30d': 'last_30_days',
  term: 'analytics_range_term',
};

/**
 * The trend's "2026-09-19" day keys as a local date on that same calendar day,
 * so formatting never slides a bar onto the neighbouring day west of UTC.
 */
export function dayKeyToDate(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

export interface PendingTestGroup {
  testId: string;
  title: string;
  /** Answers in this test still waiting for a manual grade. */
  answers: number;
}

/** The pending-grading queue folded per test, busiest first. Attempts without answers add nothing to grade. */
export function groupPendingByTest(pending: PendingAttempt[] | undefined): PendingTestGroup[] {
  const groups = new Map<string, PendingTestGroup>();
  for (const attempt of pending ?? []) {
    const answers = attempt.answers?.length ?? 0;
    if (answers === 0) continue;
    const testId = attempt.test?.id ?? '';
    const current = groups.get(testId);
    groups.set(testId, {
      testId,
      title: current?.title || attempt.test?.title || '',
      answers: (current?.answers ?? 0) + answers,
    });
  }
  return Array.from(groups.values()).sort((a, b) => b.answers - a.answers);
}

export const countPendingAnswers = (groups: PendingTestGroup[]): number =>
  groups.reduce((sum, group) => sum + group.answers, 0);
